import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

export interface StatusBadgeStyleProps {
  color: string;
  fontColor?: string;
  cornerRounding?: 'min' | 'med' | 'max' | number;
  collapse?: 'none' | 'wrap-text' | 'wrap-sides';
  capitalize?: boolean;
  uppercase?: boolean;
  fontSize?: string;
  inline?: boolean;
  hasIcon?: boolean;
  iconSize?: string;
  showTooltip?: boolean;
  align?: 'left' | 'center' | 'right';
  desktopWidth?: string;
  desktopHeight?: string;
  mobileWidth?: string;
  mobileHeight?: string;
}

/** Fills the default 30px badge inside its 4px padding. */
const DEFAULT_ICON_SIZE = '22px';

/**
 * A centred label beside an icon is balanced against it, so it sits centred over the badge.
 * Only at a fixed width — a badge that hugs its label has nothing to centre within.
 */
export function isIconBalanced({ hasIcon, align, collapse = 'none' }: StatusBadgeStyleProps): boolean {
  return !!hasIcon && !align && collapse === 'none';
}

export const useStatusBadgeStyles = makeStyles<Theme, StatusBadgeStyleProps>((theme) => ({
  wrapper: {
    cursor: ({ showTooltip }) => (showTooltip ? 'help' : 'inherit'),
    display: ({ inline }) => (inline ? 'inline-flex' : 'flex'),
    verticalAlign: 'middle',
    alignItems: 'center',
    color({ fontColor = 'white' }) {
      return fontColor;
    },
    backgroundColor: ({ color }) => color,
    padding: ({ collapse = 'none' }) => (collapse === 'wrap-text' ? '0 3px' : '4px 8px'),
    borderRadius: ({ cornerRounding = 'med' }) =>
      cornerRounding === 'min'
        ? '2px'
        : cornerRounding === 'med'
        ? '6px'
        : cornerRounding === 'max'
        ? '15px'
        : `${cornerRounding}px`,
    textAlign: 'center',
    width: ({ collapse = 'none', desktopWidth }) =>
      desktopWidth ? desktopWidth : collapse === 'none' ? '140px' : undefined,
    [theme.breakpoints.down('sm')]: {
      width: ({ collapse = 'none', mobileWidth, desktopWidth }) =>
        mobileWidth ? mobileWidth : desktopWidth ? desktopWidth : collapse === 'none' ? '110px' : undefined,
      height: ({ mobileHeight, desktopHeight }) => (mobileHeight ? mobileHeight : desktopHeight)
    },
    height: ({ collapse = 'none', fontSize = '14px', desktopHeight }) =>
      desktopHeight ? desktopHeight : collapse === 'wrap-text' ? `calc(${fontSize} + 5px)` : '30px'
  },
  iconWrapper: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  icon: ({ iconSize = DEFAULT_ICON_SIZE }) => ({
    width: iconSize,
    height: iconSize
  }),
  // Mirrors the icon, but only from space the label leaves over: it starts at nothing and grows to
  // the icon's width, so a tight badge drops the balance before it clips the label.
  iconBalance: ({ iconSize = DEFAULT_ICON_SIZE }) => ({
    flex: '1 1 0',
    maxWidth: `calc(${iconSize} + 2px)`,
    minWidth: 0
  }),
  label: {
    // float left causes the inner span (the label) to move up to align with the top of its parent (the wrapper span)
    float: 'left',
    fontSize: ({ fontSize = '14px' }) => fontSize,
    fontWeight: ({ fontSize = '14px' }) => (parseFloat(fontSize) > 12 ? 500 : 'normal'),
    paddingLeft({ align, hasIcon }) {
      if (!align && hasIcon) return '2px';
      return align === 'left' ? '8px' : undefined;
    },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // Balanced, the label hugs its text and auto margins centre it between icon and balance.
    width: (props) => (isIconBalanced(props) ? undefined : '100%'),
    flex: (props) => (isIconBalanced(props) ? '0 1 auto' : undefined),
    margin: (props) => (isIconBalanced(props) ? '0 auto' : undefined),
    minWidth: 0,
    textTransform: ({ capitalize, uppercase }) => (capitalize ? 'capitalize' : uppercase ? 'uppercase' : undefined),
    textAlign({ align = 'center' }) {
      return align;
    }
  },
  tooltip: {
    fontSize: '13px',
    minWidth: '140px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '110px'
    },
    padding: '4px 8px',
    borderRadius: '6px',
    fontWeight: 'normal',
    minHeight: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    color: ({ color }) => color,
    border: ({ color }) => `1.5px solid ${color}`
  }
}));
